import React, { useEffect, useMemo, useState } from 'react';
import { Card, Tabs, Table, Button, Space, Tag, Select, Modal, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useAuthStore } from '@/store/authStore';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import assetService from '@/services/asset.service';
import assetMaintenanceService, {
  type PmPlanDto,
  type CaseDto,
  type AmcDto,
} from '@/services/asset-maintenance.service';
import { buildAssetIdToLabelMap, formatAssetOption, resolveAssetDisplayFromMap } from '@/utils/assetDisplay';

const STATUS_COLOR: Record<string, string> = {
  open: 'blue',
  assigned: 'cyan',
  in_progress: 'processing',
  completed: 'success',
  cancelled: 'default',
  low: 'default',
  normal: 'blue',
  high: 'orange',
  urgent: 'red',
};

const AssetMaintenancePage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const canEdit = Boolean(user?.is_super_admin || user?.permissions?.includes('asset.maintenance.edit'));
  const canView = Boolean(
    user?.is_super_admin ||
      user?.permissions?.includes('asset.maintenance.view') ||
      user?.permissions?.includes('asset.view')
  );
  const assetFromUrl = sp.get('asset') || undefined;
  const tabFromUrl = sp.get('tab') || 'cases';
  const [activeTab, setActiveTab] = useState(tabFromUrl);
  const [caseStatus, setCaseStatus] = useState<string | undefined>();
  const [filterAsset, setFilterAsset] = useState<string | undefined>(assetFromUrl);

  useEffect(() => {
    if (assetFromUrl) setFilterAsset(assetFromUrl);
  }, [assetFromUrl]);

  useEffect(() => {
    setActiveTab(tabFromUrl);
  }, [tabFromUrl]);

  const { data: assets = [] } = useQuery({
    queryKey: ['assets', 'pick'],
    queryFn: () => assetService.list(),
    enabled: canView,
  });
  const assetLabels = useMemo(() => buildAssetIdToLabelMap(assets), [assets]);

  const qK = (tab: string) =>
    ['asset-maintenance', filterAsset, tab, tab === 'cases' ? caseStatus : ''] as const;

  const { data: cases = [], isLoading: lc } = useQuery({
    queryKey: qK('cases'),
    queryFn: () => assetMaintenanceService.listCases({ asset_id: filterAsset, status: caseStatus }),
    enabled: canView,
  });
  const { data: plans = [], isLoading: lp } = useQuery({
    queryKey: qK('pm'),
    queryFn: () => assetMaintenanceService.listPm({ asset_id: filterAsset }),
    enabled: canView,
  });
  const { data: amcRows = [], isLoading: la } = useQuery({
    queryKey: qK('amc'),
    queryFn: () => assetMaintenanceService.listAmc({ asset_id: filterAsset }),
    enabled: canView,
  });

  const removeCaseMut = useMutation({
    mutationFn: (id: string) => assetMaintenanceService.removeCase(id),
    onSuccess: () => {
      message.success('Work request removed');
      void queryClient.invalidateQueries({ queryKey: ['asset-maintenance'] });
    },
    onError: (e: Error) => message.error(e?.message || 'Failed'),
  });
  const removePmMut = useMutation({
    mutationFn: (id: string) => assetMaintenanceService.removePm(id),
    onSuccess: () => {
      message.success('PM plan removed');
      void queryClient.invalidateQueries({ queryKey: ['asset-maintenance'] });
    },
    onError: (e: Error) => message.error(e?.message || 'Failed'),
  });
  const removeAmcMut = useMutation({
    mutationFn: (id: string) => assetMaintenanceService.removeAmc(id),
    onSuccess: () => {
      message.success('AMC removed');
      void queryClient.invalidateQueries({ queryKey: ['asset-maintenance'] });
    },
    onError: (e: Error) => message.error(e?.message || 'Failed'),
  });

  const onFilterAsset = (v: string | null) => {
    const next = v && v.length > 0 ? v : undefined;
    setFilterAsset(next);
    setSp((prev) => {
      if (next) prev.set('asset', next);
      else prev.delete('asset');
      return prev;
    });
  };

  const onTabChange = (key: string) => {
    setActiveTab(key);
    setSp((prev) => {
      prev.set('tab', key);
      return prev;
    });
  };

  const assetQs = filterAsset ? `?asset=${filterAsset}` : '';

  const caseCols: ColumnsType<CaseDto> = useMemo(() => {
    const base: ColumnsType<CaseDto> = [
      {
        title: 'Case',
        dataIndex: 'case_number',
        render: (v: string, r) => (
          <Button type="link" onClick={() => navigate(`/asset/maintenance/cases/${r.id}`)}>
            {v}
          </Button>
        ),
      },
      { title: 'Title', dataIndex: 'title', ellipsis: true },
      {
        title: 'Asset',
        render: (_: unknown, r) => resolveAssetDisplayFromMap(assetLabels, r.asset_id),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        width: 110,
        render: (s: string) => <Tag color={STATUS_COLOR[s] || 'default'}>{s?.replace(/_/g, ' ')}</Tag>,
      },
      {
        title: 'Prio',
        dataIndex: 'priority',
        width: 90,
        render: (p: string) => <Tag color={STATUS_COLOR[p]}>{p}</Tag>,
      },
      {
        title: 'Cost (total)',
        width: 100,
        render: (_: unknown, r) => {
          const t = (Number(r.labor_cost) || 0) + (Number(r.material_cost) || 0) + (Number(r.other_cost) || 0);
          return t > 0 ? t.toFixed(0) : '—';
        },
      },
    ];
    if (canEdit) {
      base.push({
        title: 'Actions',
        width: 200,
        render: (_: unknown, r) => (
          <Space>
            <Button size="small" onClick={() => navigate(`/asset/maintenance/cases/${r.id}`)}>
              Edit
            </Button>
            <Button
              size="small"
              danger
              onClick={() => {
                void Modal.confirm({
                  title: 'Delete this work request?',
                  onOk: () => removeCaseMut.mutateAsync(r.id),
                });
              }}
            >
              Delete
            </Button>
          </Space>
        ),
      });
    }
    return base;
  }, [assetLabels, canEdit, navigate, removeCaseMut]);

  const pmColumns: ColumnsType<PmPlanDto> = useMemo(() => {
    const freqLabel = (u: string) =>
      u === 'days' ? 'day(s)' : u === 'weeks' ? 'week(s)' : u === 'months' ? 'month(s)' : 'year(s)';
    const base: ColumnsType<PmPlanDto> = [
      { title: 'Title', dataIndex: 'title' },
      {
        title: 'Asset',
        render: (_: unknown, r) => resolveAssetDisplayFromMap(assetLabels, r.asset_id),
      },
      {
        title: 'Every',
        render: (_: unknown, r) => `${r.frequency_value} ${freqLabel(r.frequency_unit)}`,
      },
      {
        title: 'Next due',
        dataIndex: 'next_due_date',
        render: (d: string | null) => (d ? dayjs(d).format('YYYY-MM-DD') : '—'),
      },
      {
        title: 'Active',
        dataIndex: 'is_active',
        width: 80,
        render: (a: boolean) => (a ? 'Yes' : 'No'),
      },
    ];
    if (canEdit) {
      base.push({
        title: 'Actions',
        width: 260,
        render: (_: unknown, r) => (
          <Space wrap>
            <Button size="small" onClick={() => navigate(`/asset/maintenance/pm-plans/${r.id}`)}>
              Edit
            </Button>
            <Button size="small" onClick={() => navigate(`/asset/maintenance/pm-plans/${r.id}/complete`)}>
              Complete
            </Button>
            <Button
              size="small"
              danger
              onClick={() => {
                void Modal.confirm({
                  title: 'Delete this PM plan?',
                  onOk: () => removePmMut.mutateAsync(r.id),
                });
              }}
            >
              Delete
            </Button>
          </Space>
        ),
      });
    }
    return base;
  }, [assetLabels, canEdit, navigate, removePmMut]);

  const amcColumns: ColumnsType<AmcDto> = useMemo(() => {
    const base: ColumnsType<AmcDto> = [
      {
        title: 'Contract',
        dataIndex: 'contract_number',
        render: (v: string | null) => v || '—',
      },
      {
        title: 'Asset',
        render: (_: unknown, r) => resolveAssetDisplayFromMap(assetLabels, r.asset_id),
      },
      {
        title: 'From',
        dataIndex: 'start_date',
        render: (d: string | null) => (d ? d.slice(0, 10) : '—'),
      },
      {
        title: 'To',
        dataIndex: 'end_date',
        render: (d: string | null) => (d ? d.slice(0, 10) : '—'),
      },
      {
        title: 'SLA (h)',
        dataIndex: 'response_sla_hours',
        width: 80,
        render: (h: number | null) => (h == null ? '—' : h),
      },
      {
        title: 'Active',
        dataIndex: 'is_active',
        width: 70,
        render: (a: boolean) => (a ? 'Yes' : 'No'),
      },
    ];
    if (canEdit) {
      base.push({
        title: 'Actions',
        width: 180,
        render: (_: unknown, r) => (
          <Space>
            <Button size="small" onClick={() => navigate(`/asset/maintenance/amc/${r.id}`)}>
              Edit
            </Button>
            <Button
              size="small"
              danger
              onClick={() => {
                void Modal.confirm({
                  title: 'Delete this AMC record?',
                  onOk: () => removeAmcMut.mutateAsync(r.id),
                });
              }}
            >
              Delete
            </Button>
          </Space>
        ),
      });
    }
    return base;
  }, [assetLabels, canEdit, navigate, removeAmcMut]);

  if (!canView) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Asset maintenance" documentNumber="" />}>
        <Card>You do not have access to asset maintenance. Required: asset.view or asset.maintenance.view</Card>
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Asset maintenance"
          documentNumber=""
          primaryActions={
            <Button onClick={() => navigate('/asset/assets')}>Back to assets</Button>
          }
        />
      }
    >
      <Space wrap style={{ marginBottom: 16 }} align="center">
        <span>Filter by asset</span>
        <Select
          showSearch
          allowClear
          optionFilterProp="label"
          placeholder="All assets"
          value={filterAsset}
          onChange={onFilterAsset as (v: string | null) => void}
          options={assets.map((a) => ({ value: a.id, label: formatAssetOption(a) }))}
          style={{ minWidth: 280 }}
        />
      </Space>
      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        items={[
          {
            key: 'cases',
            label: 'Work requests (corrective)',
            children: (
              <div>
                <Space wrap style={{ marginBottom: 12 }} align="center">
                  {canEdit && (
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => navigate(`/asset/maintenance/cases/new${assetQs}`)}
                    >
                      New work request
                    </Button>
                  )}
                  <span>Status</span>
                  <Select
                    allowClear
                    placeholder="All"
                    style={{ minWidth: 200 }}
                    value={caseStatus}
                    onChange={(v) => setCaseStatus(v || undefined)}
                    options={['open', 'assigned', 'in_progress', 'completed', 'cancelled'].map((s) => ({
                      value: s,
                      label: s.replace(/_/g, ' '),
                    }))}
                  />
                </Space>
                <Table<CaseDto>
                  rowKey="id"
                  loading={lc}
                  dataSource={cases}
                  columns={caseCols}
                  size="small"
                  pagination={false}
                />
              </div>
            ),
          },
          {
            key: 'pm',
            label: 'Preventive plans',
            children: (
              <div>
                {canEdit && (
                  <div style={{ marginBottom: 12 }}>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => navigate(`/asset/maintenance/pm-plans/new${assetQs}`)}
                    >
                      New PM plan
                    </Button>
                  </div>
                )}
                <Table<PmPlanDto>
                  rowKey="id"
                  loading={lp}
                  dataSource={plans}
                  size="small"
                  pagination={false}
                  columns={pmColumns}
                />
              </div>
            ),
          },
          {
            key: 'amc',
            label: 'AMC',
            children: (
              <div>
                {canEdit && (
                  <div style={{ marginBottom: 12 }}>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => navigate(`/asset/maintenance/amc/new${assetQs}`)}
                    >
                      New AMC
                    </Button>
                  </div>
                )}
                <Table<AmcDto>
                  rowKey="id"
                  loading={la}
                  dataSource={amcRows}
                  size="small"
                  pagination={false}
                  columns={amcColumns}
                />
              </div>
            ),
          },
        ]}
      />
    </WorkspaceLayout>
  );
};

export default AssetMaintenancePage;
